

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { message, Spin, Empty, Tag, Popconfirm, Input as AntInput } from "antd";
import {
    VideoCameraOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    UndoOutlined,
    DollarOutlined,
    ClockCircleOutlined,
    SearchOutlined,
    ExclamationCircleOutlined,
} from "@ant-design/icons";
import liveTrainingService from "../../../services/liveTraining.service";

const { Search } = AntInput;

const C = {
    blue: "#0054A6",
    blueDark: "#003B75",
    blueDeep: "#002147",
    orange: "#E8860C",
    orangeLight: "#FF8C00",
    orangeDark: "#C46F0A",
};

const LiveTrainingsList = () => {
    const navigate = useNavigate();
    const { user: currentUser } = useSelector((state) => state.auth);

    const [allTrainings, setAllTrainings] = useState([]);
    const [filteredTrainings, setFilteredTrainings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState("all");
    const [searchText, setSearchText] = useState("");

    const fetchAllTrainings = async () => {
        try {
            setLoading(true);
             const [active, deleted] = await Promise.all([
                liveTrainingService.getAllAdminLight(), // ⚡ vraiment léger : projection JPQL sans champ image
                liveTrainingService.getDeleted(),
            ]);
            const myActive = active.filter((t) => t.createdBy === currentUser.id);
            const myDeleted = deleted.filter((t) => t.createdBy === currentUser.id);
            setAllTrainings([...myActive, ...myDeleted]);
        } catch (error) {
            console.error("Erreur:", error);
            message.error("Erreur lors du chargement");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (currentUser?.id) fetchAllTrainings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser?.id]);

    useEffect(() => {
        let result = [...allTrainings];
        if (activeFilter === "active") {
            result = result.filter((t) => !t.deletedByModerator && t.approved);
        } else if (activeFilter === "pending") {
            result = result.filter((t) => !t.deletedByModerator && !t.approved);
        } else if (activeFilter === "deleted") {
            result = result.filter((t) => t.deletedByModerator);
        }
        if (searchText) {
            const lower = searchText.toLowerCase();
            result = result.filter((t) =>
                t.title?.toLowerCase().includes(lower) ||
                t.short_desc?.toLowerCase().includes(lower)
            );
        }
        setFilteredTrainings(result);
    }, [activeFilter, searchText, allTrainings]);

    const counts = {
        all: allTrainings.length,
        active: allTrainings.filter((t) => !t.deletedByModerator && t.approved).length,
        pending: allTrainings.filter((t) => !t.deletedByModerator && !t.approved).length,
        deleted: allTrainings.filter((t) => t.deletedByModerator).length,
    };

    const handleEdit = (id) => {
        navigate(`/mod/live-trainings/edit/${id}`);
    };

    const handleDelete = async (id) => {
        try {
            await liveTrainingService.softDelete(id);
            message.success("Formation supprimée");
            fetchAllTrainings();
        } catch (e) {
            message.error("Erreur");
        }
    };

    const handleRestore = async (id) => {
        try {
            await liveTrainingService.restore(id);
            message.success("Formation restaurée");
            fetchAllTrainings();
        } catch (e) {
            message.error("Erreur");
        }
    };

    const handleCardClick = (training, e) => {
        if (e.target.closest('button') || e.target.closest('a') || e.target.closest('.ant-popover')) return;
        if (training.deletedByModerator) return;
        window.open(`/live-training/${training.id}`, '_blank');
    };

    const getStatusBadge = (training) => {
        if (training.deletedByModerator) return <Tag color="red">Supprimée</Tag>;
        if (!training.approved) return <Tag color="orange">En attente</Tag>;
        return <Tag color="green">Active</Tag>;
    };

    return (
        <div className="live-list-container">
            <style>{`
                .live-list-container { min-height: 100vh; background: #f5f7fa; }
                .list-header {
                    background: linear-gradient(135deg, ${C.blueDeep} 0%, ${C.blueDark} 50%, ${C.blue} 100%);
                    padding: 32px;
                    color: white;
                    box-shadow: 0 4px 20px rgba(0,84,166,0.2);
                    position: relative;
                    overflow: hidden;
                }
                .list-header::before {
                    content: '';
                    position: absolute;
                    top: 0; left: 0;
                    width: 100%; height: 100%;
                    background-image: radial-gradient(circle at 80% 30%, ${C.orange}22 0%, transparent 50%);
                    pointer-events: none;
                }
                .list-header-content {
                    max-width: 1400px;
                    margin: 0 auto;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 16px;
                    position: relative;
                    z-index: 1;
                }
                .list-title { display: flex; align-items: center; gap: 16px; }
                .list-title h1 { margin: 0; font-size: 28px; font-weight: 700; }
                .list-title .icon {
                    font-size: 36px;
                    background: linear-gradient(135deg, ${C.orange} 0%, ${C.orangeLight} 100%);
                    padding: 12px;
                    border-radius: 12px;
                    box-shadow: 0 4px 12px ${C.orange}66;
                }
                .list-subtitle { margin: 4px 0 0 0; font-size: 14px; opacity: 0.9; }
                .btn-create {
                    background: linear-gradient(135deg, ${C.orange} 0%, ${C.orangeLight} 100%);
                    color: white; border: none;
                    padding: 12px 24px; border-radius: 30px;
                    font-size: 14px; font-weight: 600; cursor: pointer;
                    display: flex; align-items: center; gap: 8px;
                    transition: all 0.3s; box-shadow: 0 4px 12px ${C.orange}66;
                }
                .btn-create:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px ${C.orange}88;
                }
                .btn-public-view {
                    background: rgba(255,255,255,0.15); color: white;
                    border: 2px solid rgba(255,255,255,0.4);
                    padding: 10px 20px; border-radius: 30px;
                    font-size: 13px; font-weight: 600; cursor: pointer;
                    display: flex; align-items: center; gap: 8px;
                    transition: all 0.3s; backdrop-filter: blur(10px);
                }
                .btn-public-view:hover {
                    background: rgba(255,255,255,0.25);
                    border-color: rgba(255,255,255,0.6);
                    transform: translateY(-2px);
                }
                .header-actions { display: flex; gap: 12px; align-items: center; }
                .filters-bar { background: white; padding: 20px 32px; border-bottom: 1px solid #e8eaed; }
                .filters-content {
                    max-width: 1400px; margin: 0 auto;
                    display: flex; justify-content: space-between; align-items: center;
                    flex-wrap: wrap; gap: 16px;
                }
                .filter-tabs { display: flex; gap: 8px; flex-wrap: wrap; }
                .filter-tab {
                    padding: 8px 16px; border-radius: 20px;
                    background: #f5f7fa; color: #595959;
                    font-size: 13px; font-weight: 500; cursor: pointer;
                    transition: all 0.2s; display: flex; align-items: center; gap: 6px;
                }
                .filter-tab:hover { background: #f0f7ff; color: ${C.blue}; }
                .filter-tab.active {
                    background: linear-gradient(135deg, ${C.orange} 0%, ${C.orangeLight} 100%);
                    color: white; box-shadow: 0 3px 10px ${C.orange}55;
                }
                .filter-tab .count {
                    background: rgba(255,255,255,0.3); padding: 1px 8px;
                    border-radius: 10px; font-size: 11px; font-weight: 600;
                }
                .filter-tab:not(.active) .count { background: rgba(0,0,0,0.08); }
                .search-input { width: 280px; }
                .cards-container { max-width: 1400px; margin: 0 auto; padding: 32px; }
                .cards-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
                    gap: 24px;
                }
                .training-card {
                    background: white; border-radius: 16px; overflow: hidden;
                    box-shadow: 0 2px 8px rgba(0,84,166,0.08);
                    transition: all 0.3s; border: 1px solid #f0f0f0;
                    display: flex; flex-direction: column;
                    cursor: pointer; position: relative;
                }
                .training-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 30px rgba(0,84,166,0.15);
                    border-color: ${C.blue}33;
                }
                .training-card.deleted { opacity: 0.6; background: #fafafa; cursor: default; }
                .training-card.deleted:hover {
                    transform: none;
                    box-shadow: 0 2px 8px rgba(0,84,166,0.08);
                    border-color: #f0f0f0;
                }
                .card-image {
                    position: relative; width: 100%; height: 180px;
                    background: linear-gradient(135deg, ${C.blueDark} 0%, ${C.blue} 100%);
                    overflow: hidden;
                }
                .card-image img { width: 100%; height: 100%; object-fit: cover; }
                .card-image-placeholder {
                    display: flex; align-items: center; justify-content: center;
                    height: 100%; color: white; font-size: 60px; opacity: 0.5;
                }
                .live-badge {
                    position: absolute; top: 12px; left: 12px;
                    background: rgba(255,0,0,0.9); color: white;
                    padding: 4px 12px; border-radius: 20px;
                    font-size: 11px; font-weight: 700; letter-spacing: 1px;
                    display: flex; align-items: center; gap: 4px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.2); z-index: 2;
                }
                .live-badge::before {
                    content: ''; width: 8px; height: 8px;
                    background: white; border-radius: 50%;
                    animation: pulse 1.5s infinite;
                }
                @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
                .status-badge-pos { position: absolute; top: 12px; right: 12px; z-index: 2; }
                .price-tag {
                    position: absolute; bottom: 12px; right: 12px;
                    background: linear-gradient(135deg, ${C.orange} 0%, ${C.orangeLight} 100%);
                    color: white; padding: 6px 14px; border-radius: 20px;
                    font-weight: 700; font-size: 14px;
                    box-shadow: 0 4px 12px ${C.orange}55; z-index: 2;
                }
                .click-hint {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    background: rgba(0,33,71,0.85); backdrop-filter: blur(10px);
                    color: white; padding: 10px 20px; border-radius: 30px;
                    font-size: 13px; font-weight: 600;
                    opacity: 0; transition: opacity 0.3s;
                    pointer-events: none; z-index: 3;
                    display: flex; align-items: center; gap: 8px;
                    white-space: nowrap;
                }
                .training-card:not(.deleted):hover .click-hint { opacity: 1; }
                .card-body { padding: 20px; flex: 1; display: flex; flex-direction: column; }
                .card-title {
                    margin: 0 0 8px 0; font-size: 17px; font-weight: 600;
                    color: #262626; line-height: 1.4;
                    display: -webkit-box; -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical; overflow: hidden;
                }
                .card-desc {
                    color: #595959; font-size: 13px; margin: 0 0 16px 0;
                    line-height: 1.5;
                    display: -webkit-box; -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical; overflow: hidden; flex: 1;
                }
                .card-info {
                    display: flex; flex-direction: column; gap: 8px;
                    margin-bottom: 16px; padding-top: 12px;
                    border-top: 1px solid #f0f0f0;
                }
                .card-info-row { display: flex; align-items: center; gap: 8px; color: #595959; font-size: 13px; }
                .card-info-row .anticon { color: ${C.blue}; font-size: 14px; }
                .card-actions {
                    display: flex; gap: 8px;
                    padding: 16px 20px;
                    background: #fafafa;
                    border-top: 1px solid #f0f0f0;
                }
                .btn-action {
                    flex: 1; padding: 8px 12px; border-radius: 8px;
                    font-size: 13px; font-weight: 500; cursor: pointer;
                    border: 1px solid #d9d9d9;
                    background: white; color: #595959;
                    display: flex; align-items: center; justify-content: center; gap: 6px;
                    transition: all 0.2s;
                }
                .btn-action:hover { border-color: ${C.blue}; color: ${C.blue}; }
                .btn-action.btn-edit:hover { border-color: ${C.orange}; color: ${C.orange}; }
                .btn-action.btn-delete:hover { border-color: #ff4d4f; color: #ff4d4f; }
                .btn-action.btn-view:hover {
                    border-color: ${C.blue}; color: ${C.blue}; background: #f0f7ff;
                }
                .btn-action.btn-restore { background: #52c41a; color: white; border-color: #52c41a; }
                .btn-action.btn-restore:hover { background: #389e0d; border-color: #389e0d; }
                .loading-container { display: flex; align-items: center; justify-content: center; padding: 100px 20px; }
                .empty-container {
                    background: white; border-radius: 16px; padding: 80px 20px;
                    text-align: center; box-shadow: 0 2px 8px rgba(0,84,166,0.04);
                }
            `}</style>

            <div className="list-header">
                <div className="list-header-content">
                    <div className="list-title">
                        <span className="icon"><VideoCameraOutlined /></span>
                        <div>
                            <h1>🔴 Mes Formations Live</h1>
                            <p className="list-subtitle">Gérez vos formations premium</p>
                        </div>
                    </div>
                    <div className="header-actions">
                        <button
                            className="btn-public-view"
                            onClick={() => window.open('/live-trainings', '_blank')}
                        >
                            <EyeOutlined /> Vue étudiants
                        </button>
                        <button
                            className="btn-create"
                            onClick={() => navigate("/mod/create-live-training")}
                        >
                            <PlusOutlined /> Nouvelle Formation Live
                        </button>
                    </div>
                </div>
            </div>

            <div className="filters-bar">
                <div className="filters-content">
                    <div className="filter-tabs">
                        <div className={`filter-tab ${activeFilter === "all" ? "active" : ""}`} onClick={() => setActiveFilter("all")}>
                            Toutes <span className="count">{counts.all}</span>
                        </div>
                        <div className={`filter-tab ${activeFilter === "active" ? "active" : ""}`} onClick={() => setActiveFilter("active")}>
                            Actives <span className="count">{counts.active}</span>
                        </div>
                        <div className={`filter-tab ${activeFilter === "pending" ? "active" : ""}`} onClick={() => setActiveFilter("pending")}>
                            En attente <span className="count">{counts.pending}</span>
                        </div>
                        <div className={`filter-tab ${activeFilter === "deleted" ? "active" : ""}`} onClick={() => setActiveFilter("deleted")}>
                            Corbeille <span className="count">{counts.deleted}</span>
                        </div>
                    </div>

                    <Search
                        placeholder="Rechercher..."
                        allowClear
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        className="search-input"
                        prefix={<SearchOutlined />}
                    />
                </div>
            </div>

            <div className="cards-container">
                {loading ? (
                    <div className="loading-container">
                        <Spin size="large" />
                    </div>
                ) : filteredTrainings.length === 0 ? (
                    <div className="empty-container">
                        <Empty description={searchText ? "Aucun résultat" : "Aucune formation live"} />
                    </div>
                ) : (
                    <div className="cards-grid">
                        {filteredTrainings.map((training) => (
                            <div
                                key={training.id}
                                className={`training-card ${training.deletedByModerator ? "deleted" : ""}`}
                                onClick={(e) => handleCardClick(training, e)}
                            >
                                <div className="card-image">
                                    {training.thumbnail ? (
                                        <img src={training.thumbnail} alt={training.title} />
                                    ) : (
                                        <div className="card-image-placeholder">
                                            <VideoCameraOutlined />
                                        </div>
                                    )}
                                    <div className="live-badge">LIVE</div>
                                    <div className="status-badge-pos">{getStatusBadge(training)}</div>
                                    {training.price > 0 && (
                                        <div className="price-tag">
                                            <DollarOutlined /> {training.price} €
                                        </div>
                                    )}
                                    {!training.deletedByModerator && (
                                        <div className="click-hint">
                                            <EyeOutlined /> Voir la vue étudiant
                                        </div>
                                    )}
                                </div>

                                <div className="card-body">
                                    <h3 className="card-title">{training.title}</h3>
                                    <p className="card-desc">{training.short_desc || "Aucune description"}</p>

                                    <div className="card-info">
                                        {training.duration && (
                                            <div className="card-info-row">
                                                <ClockCircleOutlined />
                                                <span><strong>Durée :</strong> {training.duration}</span>
                                            </div>
                                        )}
                                        {training.categoryName && (
                                            <div className="card-info-row">
                                                <span><strong>📂 Catégorie :</strong> {training.categoryName}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="card-actions" onClick={(e) => e.stopPropagation()}>
                                    {training.deletedByModerator ? (
                                        <Popconfirm
                                            title="Restaurer cette formation ?"
                                            onConfirm={() => handleRestore(training.id)}
                                            okText="Oui"
                                            cancelText="Non"
                                        >
                                            <button className="btn-action btn-restore">
                                                <UndoOutlined /> Restaurer
                                            </button>
                                        </Popconfirm>
                                    ) : (
                                        <>
                                            <button
                                                className="btn-action btn-view"
                                                onClick={() => window.open(`/live-training/${training.id}`, '_blank')}
                                            >
                                                <EyeOutlined /> Vue étudiant
                                            </button>
                                            <button className="btn-action btn-edit" onClick={() => handleEdit(training.id)}>
                                                <EditOutlined /> Modifier
                                            </button>
                                            <Popconfirm
                                                title="Supprimer cette formation ?"
                                                onConfirm={() => handleDelete(training.id)}
                                                okText="Oui"
                                                cancelText="Non"
                                                icon={<ExclamationCircleOutlined style={{ color: "red" }} />}
                                            >
                                                <button className="btn-action btn-delete">
                                                    <DeleteOutlined />
                                                </button>
                                            </Popconfirm>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LiveTrainingsList;